import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip as LT } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Eye, AlertTriangle, Car, Users, Brain,
  Bell, TrendingUp, Shield, Camera, Radio,
  Activity, Target, Flame, MapPin
} from 'lucide-react';

const rand = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const pick = (arr) => arr[rand(0, arr.length - 1)];

const ZONES = ['Bastos','Akwa','Bonanjo','Deido','Biyem-Assi','Nlongkak','Mvog-Ada','Essos','Messa','Ngousso'];
const PLATES = ['LT-4821-A','CE-7734-B','LT-2209-X','LT-9931-C','CE-1145-D','LT-6678-F','CE-3310-G'];
const NAMES  = ['Moussa Bello','Jean-Paul Mbarga','Fatima Al-Amin','Ibrahim Daga','Sylvie Nkoa','Paul Ateba'];
const THREAT_LEVELS = {
  'CRITIQUE': {color:'text-red-400',   bg:'bg-red-500/10',    border:'border-red-500/40'},
  'ÉLEVÉ':    {color:'text-orange-400',bg:'bg-orange-500/10', border:'border-orange-500/40'},
  'MOYEN':    {color:'text-yellow-400',bg:'bg-yellow-500/10', border:'border-yellow-500/40'},
  'FAIBLE':   {color:'text-green-400', bg:'bg-green-500/10',  border:'border-green-500/40'},
};
const EVENT_TYPES = [
  {type:'Rassemblement suspect',    icon:'👥', threat:'ÉLEVÉ'},
  {type:'Véhicule volé détecté',    icon:'🚗', threat:'CRITIQUE'},
  {type:'Mouvement nocturne',       icon:'🌙', threat:'MOYEN'},
  {type:'Individu recherché',       icon:'👤', threat:'CRITIQUE'},
  {type:"Abandon d'objet suspect",  icon:'📦', threat:'ÉLEVÉ'},
  {type:'Panique de foule',         icon:'🚨', threat:'ÉLEVÉ'},
  {type:'Comportement agressif',    icon:'⚡', threat:'ÉLEVÉ'},
  {type:'Circulation anormale',     icon:'🔄', threat:'MOYEN'},
];
const RECOMMENDATIONS = {
  'CRITIQUE': "Déployer unité d'intervention immédiatement · Alerter commissariat de zone",
  'ÉLEVÉ':    'Envoyer 2 patrouilles dans les 5 minutes · Augmenter surveillance',
  'MOYEN':    'Surveiller et documenter · Patrouille de routine recommandée',
  'FAIBLE':   'Aucune action immédiate requise · Continuer surveillance standard',
};
const CITY_ZONES = [
  {name:'Akwa',       x:15,y:20,base:75},
  {name:'Bonanjo',    x:25,y:35,base:40},
  {name:'Deido',      x:40,y:15,base:85},
  {name:'Bastos',     x:60,y:20,base:55},
  {name:'Nlongkak',   x:70,y:35,base:60},
  {name:'Biyem-Assi', x:55,y:55,base:35},
  {name:'Mvog-Ada',   x:75,y:60,base:70},
  {name:'Essos',      x:85,y:40,base:45},
  {name:'Messa',      x:30,y:55,base:50},
  {name:'Ngousso',    x:45,y:70,base:30},
  {name:'Omnisport',  x:60,y:75,base:65},
  {name:'Mendong',    x:20,y:75,base:25},
];

const useAlerts = () => {
  const [alerts, setAlerts] = React.useState([]);
  React.useEffect(() => {
    const seed = () => { const ev = pick(EVENT_TYPES); return { id: Date.now()+rand(0,9999), zone:pick(ZONES), ...ev, time:new Date().toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit',second:'2-digit'}), score:rand(45,99) }; };
    setAlerts([seed(),seed(),seed(),seed(),seed(),seed(),seed(),seed()]);
    const t = setInterval(() => setAlerts(a => [seed(),...a.slice(0,24)]), 4000);
    return () => clearInterval(t);
  }, []);
  return alerts;
};

const useKPIs = () => {
  const [kpis, setKPIs] = React.useState({cameras:47,alerts:0,faces:0,plates:0,threats:0});
  React.useEffect(() => {
    setKPIs(k => ({...k,alerts:rand(12,28),faces:rand(3,9),plates:rand(5,14),threats:rand(2,6)}));
    const t = setInterval(() => setKPIs(k => ({...k,alerts:k.alerts+(Math.random()>0.6?1:0),faces:k.faces+(Math.random()>0.8?1:0),plates:k.plates+(Math.random()>0.7?1:0),threats:k.threats+(Math.random()>0.9?1:0)})), 3000);
    return () => clearInterval(t);
  }, []);
  return kpis;
};

const useCityHeat = () => {
  const [zones, setZones] = React.useState(CITY_ZONES.map(z => ({...z,level:z.base})));
  React.useEffect(() => {
    const t = setInterval(() => setZones(z => z.map(zone => ({...zone,level:Math.max(5,Math.min(100,zone.level+rand(-8,8)))}))), 2500);
    return () => clearInterval(t);
  }, []);
  return zones;
};

const heatColor = (v) => {
  if(v>80) return {fill:'#ef4444',glow:'rgba(239,68,68,0.4)',ring:'#ef4444'};
  if(v>60) return {fill:'#f97316',glow:'rgba(249,115,22,0.35)',ring:'#f97316'};
  if(v>40) return {fill:'#eab308',glow:'rgba(234,179,8,0.3)',ring:'#eab308'};
  if(v>20) return {fill:'#3b82f6',glow:'rgba(59,130,246,0.25)',ring:'#3b82f6'};
  return {fill:'#334155',glow:'rgba(51,65,85,0.2)',ring:'#475569'};
};

const CityHeatmap = () => {
  const zones = useCityHeat();
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setTick(p => p + 1), 80);
    return () => clearInterval(t);
  }, []);

  // Real GPS coordinates for Douala & Yaoundé neighborhoods
  const ZONE_COORDS: Record<string, [number, number]> = {
    'Akwa':       [4.052,  9.703],
    'Bonanjo':    [4.042,  9.695],
    'Deido':      [4.068,  9.715],
    'Messa':      [4.030,  9.688],
    'Mendong':    [3.998,  9.678],
    'Bastos':     [3.898, 11.528],
    'Nlongkak':   [3.878, 11.545],
    'Biyem-Assi': [3.852, 11.508],
    'Mvog-Ada':   [3.848, 11.558],
    'Essos':      [3.868, 11.568],
    'Ngousso':    [3.838, 11.522],
    'Omnisport':  [3.845, 11.512],
  };

  const getStyle = (v: number) => {
    if(v>80) return {color:'#ef4444',fillColor:'#ef4444'};
    if(v>60) return {color:'#f97316',fillColor:'#f97316'};
    if(v>40) return {color:'#eab308',fillColor:'#eab308'};
    if(v>20) return {color:'#3b82f6',fillColor:'#3b82f6'};
    return {color:'#334155',fillColor:'#334155'};
  };

  const getLabel = (v: number) =>
    v>80?'CRITIQUE':v>60?'ÉLEVÉ':v>40?'MOYEN':v>20?'FAIBLE':'STABLE';

  const pulse = Math.sin((tick / 60) * Math.PI * 2) * 0.5 + 0.5;

  return (
    <div className="relative w-full rounded-xl overflow-hidden border border-blue-900/40" style={{height:'520px'}}>
      <MapContainer
        center={[5.5, 12.5]}
        zoom={5}
        minZoom={4}
        maxZoom={12}
        style={{height:'520px', width:'100%', background:'#020617'}}
        zoomControl={true}
        scrollWheelZoom={true}
        attributionControl={false}>

        {/* Dark map tiles */}
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          attribution=""
        />

        {/* Zone markers */}
        {zones.map((zone, i) => {
          const coords = ZONE_COORDS[zone.name];
          if(!coords) return null;
          const style = getStyle(zone.level);
          const r = Math.max(8, Math.min(22, 8 + zone.level / 6));
          const pulseR = r + pulse * (zone.level > 60 ? 10 : 5);

          return (
            <React.Fragment key={zone.name}>
              {/* Outer pulsing ring */}
              <CircleMarker
                center={coords}
                radius={pulseR}
                pathOptions={{
                  color: style.color,
                  fillColor: style.fillColor,
                  fillOpacity: 0.08,
                  weight: 1.5,
                  opacity: 0.3 + pulse * 0.5,
                }}/>
              {/* Main threat circle */}
              <CircleMarker
                center={coords}
                radius={r}
                pathOptions={{
                  color: style.color,
                  fillColor: style.fillColor,
                  fillOpacity: 0.7,
                  weight: 2,
                }}>
                <LT direction="top" permanent={false} opacity={1}>
                  <div style={{background:'#0f172a',border:`1px solid ${style.color}`,borderRadius:'6px',padding:'4px 8px',minWidth:'120px'}}>
                    <p style={{color:'white',fontWeight:'bold',fontSize:'11px',margin:0}}>{zone.name}</p>
                    <p style={{color:style.color,fontSize:'10px',margin:0}}>{Math.round(zone.level)}/100 · {getLabel(zone.level)}</p>
                    <p style={{color:'#64748b',fontSize:'9px',margin:0}}>{zone.level>90?'Intervention immédiate':zone.level>60?'Surveillance renforcée':zone.level>40?'Patrouille recommandée':'Surveillance standard'}</p>
                  </div>
                </LT>
              </CircleMarker>
            </React.Fragment>
          );
        })}
      </MapContainer>

      {/* Live badge */}
      <div className="absolute top-2 left-10 z-[1000] flex items-center gap-1.5 bg-slate-900/90 px-2 py-1 rounded-lg border border-blue-900/40">
        <div className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse"/>
        <span style={{fontSize:'8px',fontWeight:'bold',letterSpacing:'0.12em',color:'#94a3b8',textTransform:'uppercase'}}>
          Douala &amp; Yaoundé · Menaces actives
        </span>
      </div>

      {/* Legend */}
      <div className="absolute top-2 right-2 z-[1000] flex flex-col gap-0.5 bg-slate-900/90 px-2 py-1.5 rounded-lg border border-blue-900/40">
        {[['CRITIQUE','#ef4444'],['ÉLEVÉ','#f97316'],['MOYEN','#eab308'],['FAIBLE','#3b82f6']].map(([l,c])=>(
          <div key={l} className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full" style={{background:c}}/>
            <span style={{color:'#64748b',fontSize:'8px'}}>{l}</span>
          </div>
        ))}
      </div>

      {/* Stats bar */}
      <div className="absolute bottom-0 left-0 right-0 z-[1000] flex items-center justify-around px-3 py-1.5 border-t border-blue-900/30"
        style={{background:'rgba(2,6,23,0.92)',backdropFilter:'blur(8px)'}}>
        {[
          {label:'CRITIQUE',count:zones.filter(z=>z.level>80).length,  color:'#ef4444'},
          {label:'ÉLEVÉ',   count:zones.filter(z=>z.level>60&&z.level<=80).length,color:'#f97316'},
          {label:'MOYEN',   count:zones.filter(z=>z.level>40&&z.level<=60).length,color:'#eab308'},
          {label:'STABLE',  count:zones.filter(z=>z.level<=40).length, color:'#3b82f6'},
        ].map(s=>(
          <div key={s.label} className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{background:s.color}}/>
            <span style={{color:s.color,fontSize:'9px',fontWeight:'bold'}}>{s.count}</span>
            <span style={{color:'#475569',fontSize:'8px'}}>{s.label}</span>
          </div>
        ))}
        <span style={{color:'#334155',fontSize:'8px'}}>{zones.length} zones surveillées</span>
      </div>
    </div>
  );
};


const ThreatRing = ({score,label,color}) => {
  const r=28, circ=2*Math.PI*r;
  return (
    <div className="flex flex-col items-center gap-1">
      <svg width="72" height="72" className="-rotate-90">
        <circle cx="36" cy="36" r={r} fill="none" stroke="#1e293b" strokeWidth="6"/>
        <circle cx="36" cy="36" r={r} fill="none" stroke={color} strokeWidth="6" strokeDasharray={circ} strokeDashoffset={circ*(1-score/100)} strokeLinecap="round" style={{transition:'stroke-dashoffset 1s ease'}}/>
        <text x="36" y="36" textAnchor="middle" dominantBaseline="central" fill="white" fontSize="14" fontWeight="bold" transform="rotate(90,36,36)">{score}</text>
      </svg>
      <p className="text-xs text-slate-400 text-center">{label}</p>
    </div>
  );
};

const PulseDot = ({color}) => (
  <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${color}`}/>
    <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${color}`}/>
  </span>
);

const Section = ({icon:Icon,title,children,accent='text-blue-400',className=''}) => (
  <div className={`rounded-xl border border-blue-900/40 bg-slate-800/80 overflow-hidden shadow-lg flex flex-col ${className}`}>
    <div className="flex items-center gap-2 px-4 py-3 bg-slate-700/80 border-b border-blue-900/40 flex-shrink-0">
      <Icon className={`w-4 h-4 ${accent}`}/>
      <span className="text-sm font-semibold text-white">{title}</span>
    </div>
    <div className="p-4 flex-1">{children}</div>
  </div>
);

export const SmartVisionPage = ({ user }) => {
  const alerts = useAlerts();
  const kpis   = useKPIs();
  const [selectedAlert, setSelectedAlert] = React.useState(null);
  const [threatScores, setThreatScores] = React.useState({global:72,facial:58,lpr:44,behavior:81});
  React.useEffect(() => {
    const t = setInterval(() => setThreatScores(s => ({
      global:   Math.max(30,Math.min(99,s.global+rand(-3,3))),
      facial:   Math.max(20,Math.min(99,s.facial+rand(-4,4))),
      lpr:      Math.max(20,Math.min(99,s.lpr+rand(-3,3))),
      behavior: Math.max(30,Math.min(99,s.behavior+rand(-4,4))),
    })), 4000);
    return () => clearInterval(t);
  }, []);
  const topAlert  = alerts[0];
  const topThreat = topAlert ? THREAT_LEVELS[topAlert.threat] : null;
  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Eye className="w-6 h-6 text-blue-400"/>
            <h1 className="text-2xl font-bold text-white">SmartVision</h1>
          </div>
          <p className="text-slate-400 text-sm">Surveillance urbaine intelligente · Yaoundé & Douala · DGSN</p>
        </div>
        <div className="flex items-center gap-2">
          <PulseDot color="bg-green-400"/>
          <span className="text-green-400 text-xs font-medium">Système actif</span>
          <span className="text-slate-500 text-xs">{new Date().toLocaleTimeString('fr-FR')}</span>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          {icon:Camera,        label:'Caméras actives',     value:kpis.cameras, color:'text-blue-400',   bg:'bg-blue-500/10',   border:'border-blue-500/30'},
          {icon:Bell,          label:'Alertes (session)',   value:kpis.alerts,  color:'text-orange-400', bg:'bg-orange-500/10', border:'border-orange-500/30'},
          {icon:Users,         label:'Visages détectés',    value:kpis.faces,   color:'text-purple-400', bg:'bg-purple-500/10', border:'border-purple-500/30'},
          {icon:Car,           label:'Plaques identifiées', value:kpis.plates,  color:'text-yellow-400', bg:'bg-yellow-500/10', border:'border-yellow-500/30'},
          {icon:AlertTriangle, label:'Menaces actives',     value:kpis.threats, color:'text-red-400',    bg:'bg-red-500/10',    border:'border-red-500/30'},
        ].map((k,i) => (
          <div key={i} className={`rounded-xl p-3 border ${k.border} ${k.bg} flex items-center gap-3`}>
            <k.icon className={`w-5 h-5 ${k.color} flex-shrink-0`}/>
            <div><p className="text-white font-bold text-lg leading-none">{k.value}</p><p className="text-slate-400 text-xs mt-0.5">{k.label}</p></div>
          </div>
        ))}
      </div>
      {topAlert && topThreat && (
        <div className={`rounded-xl border ${topThreat.border} ${topThreat.bg} p-4 flex items-start gap-3`}>
          <PulseDot color={topAlert.threat==='CRITIQUE'?'bg-red-400':'bg-orange-400'}/>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${topThreat.border} ${topThreat.color}`}>{topAlert.threat}</span>
              <span className="text-white font-semibold text-sm">{topAlert.type}</span>
            </div>
            <p className="text-slate-300 text-xs">Zone: <span className="text-white font-medium">{topAlert.zone}</span> · Score: <span className={topThreat.color+' font-bold'}>{topAlert.score}/100</span> · {topAlert.time}</p>
            <p className="text-slate-400 text-xs mt-1">🤖 IA recommande: <span className="text-yellow-300">{RECOMMENDATIONS[topAlert.threat]}</span></p>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Section icon={Flame} title="Carte thermique — menaces actives" accent="text-orange-400" className="h-full">
            <CityHeatmap/>
          </Section>
        </div>
        <div className="space-y-4">
          <Section icon={Target} title="Scores de menace" accent="text-red-400">
            <div className="grid grid-cols-2 gap-3">
              <ThreatRing score={threatScores.global}   label="Global"       color="#ef4444"/>
              <ThreatRing score={threatScores.behavior} label="Comportement" color="#f97316"/>
              <ThreatRing score={threatScores.facial}   label="Facial"       color="#a855f7"/>
              <ThreatRing score={threatScores.lpr}      label="Plaques"      color="#eab308"/>
            </div>
          </Section>
          <Section icon={Radio} title="Statut des systèmes" accent="text-green-400">
            <div className="space-y-2">
              {['Moteur YOLO v8','Reconnaissance faciale','Module LPR','IA Comportementale','Flux temps réel','Base watchlist'].map((s,i)=>(
                <div key={i} className="flex items-center justify-between">
                  <span className="text-slate-300 text-xs">{s}</span>
                  <div className="flex items-center gap-1.5"><PulseDot color="bg-green-400"/><span className="text-green-400 text-xs font-medium">Actif</span></div>
                </div>
              ))}
            </div>
          </Section>
        </div>
      </div>
      {/* ROW 2: Alert Feed (2/3) + AI Recommendation (1/3) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
        <div className="lg:col-span-2">
          <Section icon={Activity} title="Flux d'événements en temps réel" accent="text-red-400" className="h-full">
            <div className="space-y-2 overflow-y-auto" style={{maxHeight:'320px'}}>
              {alerts.map((a,i) => {
                const tl = THREAT_LEVELS[a.threat];
                return (
                  <div key={a.id} onClick={()=>setSelectedAlert(a)} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer hover:bg-slate-700/50 transition-all ${i===0?'bg-slate-700/40 border-slate-600/60':'border-transparent'}`}>
                    <span className="text-base">{a.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${tl.bg} ${tl.color} border ${tl.border}`}>{a.threat}</span>
                        <span className="text-white text-xs font-medium truncate">{a.type}</span>
                      </div>
                      <p className="text-slate-400 text-xs">{a.zone} · {a.time}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={`text-sm font-bold ${tl.color}`}>{a.score}</p>
                      <p className="text-slate-500 text-xs">score</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        </div>
        <div className="space-y-4">
          <Section icon={Brain} title="Recommandation IA" accent="text-yellow-400">
            {selectedAlert ? (() => {
              const tl = THREAT_LEVELS[selectedAlert.threat];
              return (
                <div className="space-y-3">
                  <div className={`rounded-lg border ${tl.border} ${tl.bg} p-3`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">{selectedAlert.icon}</span>
                      <span className="text-white font-semibold text-sm">{selectedAlert.type}</span>
                      <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded-full border ${tl.border} ${tl.color}`}>{selectedAlert.threat}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><p className="text-slate-400">Zone</p><p className="text-white font-medium">{selectedAlert.zone}</p></div>
                      <div><p className="text-slate-400">Score</p><p className={`font-bold ${tl.color}`}>{selectedAlert.score}/100</p></div>
                      <div><p className="text-slate-400">Heure</p><p className="text-white">{selectedAlert.time}</p></div>
                      <div><p className="text-slate-400">Escalade</p><p className="text-orange-400 font-bold">{rand(45,92)}%</p></div>
                    </div>
                  </div>
                  <div className="rounded-lg bg-yellow-500/10 border border-yellow-500/30 p-3">
                    <p className="text-yellow-400 text-xs font-bold uppercase tracking-wider mb-2">🤖 Action recommandée</p>
                    <p className="text-white text-xs leading-relaxed">{RECOMMENDATIONS[selectedAlert.threat]}</p>
                  </div>
                  <button onClick={()=>setSelectedAlert(null)} className="w-full py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs transition-colors">Fermer</button>
                </div>
              );
            })() : (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <Brain className="w-8 h-8 text-slate-600 mb-2"/>
                <p className="text-slate-500 text-xs">Cliquez sur une alerte pour obtenir une recommandation</p>
              </div>
            )}
          </Section>
        </div>
      </div>
      {/* ROW 3: Predictive + Facial + LPR */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Section icon={TrendingUp} title="Analytique prédictive" accent="text-green-400">
          <div className="space-y-3">
            {[
              {zone:'Akwa',    prob:84,type:'Vol à main armée',      time:'20h–01h Ven'},
              {zone:'Bastos',  prob:61,type:'Intrusion véhicule',    time:'22h–02h Sam'},
              {zone:'Mvog-Ada',prob:73,type:'Rassemblement illicite', time:'18h–21h Jeu'},
              {zone:'Deido',   prob:55,type:'Comportement suspect',  time:'21h–23h Mer'},
            ].map((p,i)=>(
              <div key={i} className="rounded-lg bg-slate-700/40 border border-slate-600/40 p-2.5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-white text-xs font-medium">{p.zone}</span>
                  <span className={`text-xs font-bold ${p.prob>75?'text-red-400':p.prob>60?'text-orange-400':'text-yellow-400'}`}>{p.prob}%</span>
                </div>
                <div className="w-full bg-slate-600 rounded-full h-1.5 mb-1">
                  <div className={`h-1.5 rounded-full ${p.prob>75?'bg-red-500':p.prob>60?'bg-orange-500':'bg-yellow-500'}`} style={{width:`${p.prob}%`}}/>
                </div>
                <p className="text-slate-400 text-xs">{p.type} · {p.time}</p>
              </div>
            ))}
          </div>
        </Section>
        <Section icon={Users} title="Reconnaissance faciale" accent="text-purple-400">
          <div className="space-y-2">
            {Array.from({length:5},(_,i)=>({name:pick(NAMES),conf:rand(72,99),status:pick(['Recherché','Inconnu','Signalé']),time:new Date(Date.now()-i*rand(30,300)*1000).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'}),zone:pick(ZONES)})).map((f,i)=>(
              <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-slate-700/40 border border-slate-600/40">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{f.name.split(' ').map(n=>n[0]).join('')}</div>
                <div className="flex-1 min-w-0"><p className="text-white text-xs font-medium truncate">{f.name}</p><p className="text-slate-400 text-xs">{f.zone} · {f.time}</p></div>
                <div className="text-right flex-shrink-0"><p className={`text-xs font-bold ${f.status==='Recherché'?'text-red-400':f.status==='Signalé'?'text-orange-400':'text-slate-400'}`}>{f.status}</p><p className="text-slate-500 text-xs">{f.conf}%</p></div>
              </div>
            ))}
          </div>
        </Section>
        <Section icon={Car} title="Reconnaissance de plaques" accent="text-yellow-400">
          <div className="space-y-2">
            {Array.from({length:5},(_,i)=>({plate:pick(PLATES),status:pick(['Volé','Suspect','Blacklist','Normal']),conf:rand(85,99),zone:pick(ZONES),time:new Date(Date.now()-i*rand(30,200)*1000).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'})})).map((p,i)=>(
              <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-slate-700/40 border border-slate-600/40">
                <div className="px-2 py-1 rounded bg-yellow-500/20 border border-yellow-500/30 font-mono text-yellow-300 text-xs font-bold flex-shrink-0">{p.plate}</div>
                <div className="flex-1 min-w-0"><p className="text-white text-xs font-medium">{p.zone}</p><p className="text-slate-400 text-xs">{p.time}</p></div>
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded border flex-shrink-0 ${p.status==='Volé'||p.status==='Blacklist'?'text-red-400 bg-red-500/10 border-red-500/30':p.status==='Suspect'?'text-orange-400 bg-orange-500/10 border-orange-500/30':'text-green-400 bg-green-500/10 border-green-500/30'}`}>{p.status}</span>
              </div>
            ))}
          </div>
        </Section>
      </div>
      <div className="rounded-xl border border-blue-900/30 bg-slate-800/40 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-slate-400 text-xs italic">NEXUS360 SmartVision transforme les caméras de surveillance passives en actifs d'intelligence proactive capables de détecter, prédire et aider à neutraliser les menaces sécuritaires urbaines en temps réel.</p>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Shield className="w-4 h-4 text-blue-400"/>
          <span className="text-blue-300 text-xs font-medium">Souverain · Sur site · Données au Cameroun</span>
        </div>
      </div>
    </div>
  );
};